// Ordering for the SRA Results table.
//
// The default is the order the answers were GATHERED in. Rows arrive from the
// results endpoint in no guaranteed order, so chronological is applied here
// rather than assumed — before this, a long survey's answers read in whatever
// order the query happened to return them, which is not an order at all.
//
// Authors of long surveys need other orders too, so every column is sortable.
// Sorting is three-state per column: ascending, descending, then back to the
// gathered order. That third state is the point — chronological is the
// meaningful default and there has to be a way back to it.
//
// UI-FREE: no React. Safe to unit-test in isolation, which is why it lives
// here rather than inside the component that renders the table.

import { mapNonAnswer, extractGcsPath } from './exploreData';

/**
 * When an answer was gathered, in epoch milliseconds.
 * @returns {number|null} null when the row carries no usable timestamp.
 */
export function answeredAt(row) {
    const raw = row?.created_at ?? row?.answered_at ?? row?.updated_at;
    if (!raw) return null;
    const ms = Date.parse(raw);
    return Number.isFinite(ms) ? ms : null;
}

/**
 * Build the per-column sort key reader.
 *
 * Three of the six cells render as elements — a confidence chip, a play arrow,
 * a file tag — so each column needs a plain scalar. These deliberately mirror
 * what the cell SHOWS: sorting Value by a raw JSON blob while the author reads
 * a filename would look broken.
 *
 * @param {Object} ctx
 * @param {Object} ctx.fileMeta   gcsPath -> { filename, ... }, for Value/Upload
 * @param {Object} ctx.textByKey  variable_name -> question text, for Question
 */
export function makeSortValue({ fileMeta = {}, textByKey = {} } = {}) {
    return function sortValue(row, key) {
        switch (key) {
            case 'Variable':
                return String(row?.variable_name || '').toLowerCase();

            case 'Question':
                return String(textByKey[String(row?.variable_name)] || '').toLowerCase();

            case 'Value': {
                const val = row?.value_json;
                if (val === null || val === undefined) return '';
                const gcsPath = extractGcsPath(val);
                if (gcsPath) {
                    const meta = fileMeta[gcsPath];
                    return String(meta?.filename || decodeURIComponent(gcsPath.split('/').pop() || gcsPath)).toLowerCase();
                }
                if (typeof val === 'string') return mapNonAnswer(val).toLowerCase();
                if (typeof val === 'number' || typeof val === 'boolean') return String(val).toLowerCase();
                return JSON.stringify(val).toLowerCase();
            }

            case 'Conf.': {
                const num = Number(row?.confidence);
                // An unscored row sorts below every scored one rather than
                // alongside 0%, which would read as a real low score.
                return Number.isFinite(num) ? num : -1;
            }

            // Voice and Upload are presence, not content: sorting groups the
            // answers that have a recording or a file against those that don't.
            case 'Voice':
                return row?.audio_storage_path ? 1 : 0;
            case 'Upload':
                return extractGcsPath(row?.value_json) ? 1 : 0;

            default:
                return '';
        }
    };
}

/**
 * Order the rows for display.
 *
 * @param {Array} rows
 * @param {{key: string, dir: 'asc'|'desc'}|null} sort  null = gathered order
 * @param {Function} sortValue  from makeSortValue
 * @returns {Array} a new array; the input is not mutated
 */
export function sortResultRows(rows, sort, sortValue) {
    const indexed = (Array.isArray(rows) ? rows : []).map((row, index) => ({ row, index }));

    if (!sort || !sort.key) {
        indexed.sort((a, b) => {
            const ta = answeredAt(a.row);
            const tb = answeredAt(b.row);
            // Undated rows keep their arrival order and sink below the dated
            // ones, so a missing timestamp never reorders answers that have one.
            if (ta === null && tb === null) return a.index - b.index;
            if (ta === null) return 1;
            if (tb === null) return -1;
            return ta - tb || a.index - b.index;
        });
        return indexed.map(entry => entry.row);
    }

    const direction = sort.dir === 'desc' ? -1 : 1;
    indexed.sort((a, b) => {
        const va = sortValue(a.row, sort.key);
        const vb = sortValue(b.row, sort.key);
        if (va < vb) return -direction;
        if (va > vb) return direction;
        // Ties keep the gathered order, so a sort on a coarse column like Voice
        // still reads chronologically within each group.
        return a.index - b.index;
    });
    return indexed.map(entry => entry.row);
}

/**
 * The gathered order on its own, for callers that have no sort state to apply.
 *
 * The Results CSV uses this: sraExports' rule is that an export covers what its
 * view is SHOWING, and the view now shows chronological by default. Without it
 * the table and its own export disagree about the order of the same rows.
 *
 * A per-card sort an author has clicked is NOT reflected — that state lives in
 * each recipient's table and there are many of them, so there is no single
 * "current order" for a whole export to follow.
 */
export function sortByGathered(rows) {
    return sortResultRows(rows, null, null);
}

/**
 * Ascending -> descending -> gathered order, per column. Moving to a different
 * column always starts ascending.
 */
export function nextSortState(current, key) {
    if (!current || current.key !== key) return { key, dir: 'asc' };
    if (current.dir === 'asc') return { key, dir: 'desc' };
    return null;
}
